import { ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { Button, Input, Popover, Select, Table, Tag } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGetAssociatedCompanies from '../../../hooks/graphql/useGetAssociatedCompanies';
import { useGetCompanyUsers } from '../../../hooks/graphql/useGetCompanyUsers';
import { useUpdateUserStatus } from '../../../hooks/graphql/useUpdateUserStatus';
import { useEffectOnce } from '../../../hooks/utils/useEffectOnce';
import { debounce } from '../../../utils/debounce';
import Can from '../../common/Can';
import { PERMISSION_CODES } from '../../../constants/permissionCodes';
import './UserListSection.scss';

interface IAssociatedCompanies {
  abbreviatedCompanyName: string;
  companyId: number;
  companyName: string;
}

const UserListSection: React.FC = () => {
  const { t } = useTranslate();
  const [visiblePopoverKey, setVisiblePopoverKey] = useState<number | null>(
    null,
  );
  const [associatedCompaniesValue, setAssociatedCompaniesValue] = useState<
    string[]
  >([]);
  const [searchText, setSearchText] = useState<string>('');
  const { associatedCompanies, getAssociatedCompanies } =
    useGetAssociatedCompanies();
  const { companyUsers, getCompanyUsers, loading } = useGetCompanyUsers();
  const { updateStatus, loading: updateStatusLoading } = useUpdateUserStatus();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const getCompanyIds = useCallback(
    (companyNames: string[]): number[] => {
      if (!associatedCompanies) return [];
      return associatedCompanies
        .filter((comp: IAssociatedCompanies) =>
          companyNames.includes(comp.companyName),
        )
        .map((com: IAssociatedCompanies) => com.companyId);
    },
    [associatedCompanies],
  );

  const updateUserStatus = (employeeId: number, active: boolean) => {
    updateStatus(Number(employeeId), !active, () => {
      setVisiblePopoverKey(null);
      const companyIds = getCompanyIds(associatedCompaniesValue);
      getCompanyUsers({
        variables: {
          filter: {
            companyId: companyIds,
            search: searchText,
          },
          pagination: { page: pagination.current, limit: pagination.pageSize },
          sort: { name: 'familyName', order: 'ASC' },
        },
        fetchPolicy: 'network-only',
      });
    });
  };

  useEffectOnce(() => {
    getAssociatedCompanies();
  });

  useEffect(() => {
    if (associatedCompanies) {
      const companyIds = associatedCompanies.map(
        (com: IAssociatedCompanies) => com.companyId,
      );
      setAssociatedCompaniesValue(
        associatedCompanies?.map(
          (compa: IAssociatedCompanies) => compa.companyName,
        ),
      );

      getCompanyUsers({
        variables: {
          filter: {
            companyId: companyIds,
            search: searchText,
          },
          pagination: { page: 1, limit: 10 },
          sort: { name: 'familyName', order: 'ASC' },
        },
      });
    }
  }, [associatedCompanies]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string, companyNames: string[]) => {
        const companyIds = getCompanyIds(companyNames);
        getCompanyUsers({
          variables: {
            filter: {
              companyId: companyIds,
              search: value,
            },
            pagination: {
              page: pagination.current,
              limit: pagination.pageSize,
            },
            sort: { name: 'familyName', order: 'ASC' },
          },
        });
      }, 300),
    [getCompanyIds, getCompanyUsers, pagination.current, pagination.pageSize],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value, associatedCompaniesValue);
  };

  const handlePopoverVisible = (key: number | null) => {
    setVisiblePopoverKey(key);
  };

  const handleAssociatedCompanyChange = (eventValue: string[]) => {
    setAssociatedCompaniesValue(eventValue);

    const companyIds = getCompanyIds(eventValue);
    getCompanyUsers({
      variables: {
        filter: {
          companyId: companyIds,
          search: searchText,
        },
        pagination: { page: pagination.current, limit: pagination.pageSize },
        sort: { name: 'familyName', order: 'ASC' },
      },
    });
  };

  const handlePageChange = (newPagination: TablePaginationConfig) => {
    const companyIds = getCompanyIds(associatedCompaniesValue);
    const newCurrent = newPagination.current ?? pagination.current;
    const newPageSize = newPagination.pageSize ?? pagination.pageSize;

    setPagination({
      current: newCurrent,
      pageSize: newPageSize,
    });
    getCompanyUsers({
      variables: {
        filter: {
          companyId: companyIds,
          search: searchText,
        },
        pagination: {
          page: newCurrent,
          limit: newPageSize,
        },
        sort: { name: 'familyName', order: 'ASC' },
      },
    });
  };

  const handlePageSizeChange = (pageNo: number, pageSize: number) => {
    const companyIds = getCompanyIds(associatedCompaniesValue);

    getCompanyUsers({
      variables: {
        filter: {
          companyId: companyIds,
          search: searchText,
        },
        pagination: { page: pageNo, limit: pageSize },
        sort: { name: 'familyName', order: 'ASC' },
      },
    });
  };

  const columns = [
    {
      title: <T keyName='name' />,
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: <T keyName='first-name' />,
      dataIndex: 'familyName',
      key: 'familyName',
    },
    {
      title: <T keyName='email-address' />,
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: <T keyName='account-status' />,
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: { employeeId: number }) => (
        <Can permission={PERMISSION_CODES.EMPLOYEES_SET_STATUS} mode='disable'>
          <Popover
            content={
              <div className='popover-content'>
                <p className='message'>
                  <ExclamationCircleOutlined className='icon' />
                  {t('Please confirm that you want to')}{' '}
                  {t(active ? 'deactivate' : 'activate')} {t('this account')}
                </p>
                <div className='actions'>
                  <Button
                    className='no-button'
                    onClick={() => setVisiblePopoverKey(null)}
                  >
                    {t('no')}
                  </Button>
                  <Button
                    className='yes-button'
                    onClick={() => updateUserStatus(record.employeeId, active)}
                    loading={updateStatusLoading}
                  >
                    {t('yes')}
                  </Button>
                </div>
              </div>
            }
            title=''
            trigger='click'
            open={visiblePopoverKey === record.employeeId}
            onOpenChange={visible =>
              handlePopoverVisible(visible ? record.employeeId : null)
            }
          >
            <Tag color={active ? 'green' : 'red'} style={{ cursor: 'pointer' }}>
              {active ? t('Active') : t('Inactive')}
            </Tag>
          </Popover>
        </Can>
      ),
    },
  ];

  return (
    <div className='user-list-section-container'>
      <div className='user-list-company-container'>
        <h1>{t('list-of-employees')}</h1>
      </div>
      <div className='user-list-header-container'>
        <Input
          className='user-list-search-bar'
          placeholder={t('name-search-text')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={handleSearch}
        />
        <Select
          mode='multiple'
          maxTagCount='responsive'
          className='user-list-select-bar'
          value={associatedCompaniesValue}
          showSearch
          placeholder={t('select-a-company')}
          filterOption={(input, option) =>
            (option?.companyName ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          options={associatedCompanies}
          fieldNames={{ label: 'companyName', value: 'companyName' }}
          onChange={handleAssociatedCompanyChange}
        />
      </div>
      <div className='user-list-table-container'>
        <Table
          columns={columns}
          dataSource={companyUsers?.list}
          bordered={true}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            onShowSizeChange: (current, size) => {
              setPagination({ ...pagination, pageSize: size });
              handlePageSizeChange(current, size);
            },
            total: companyUsers?.count ?? 1,
          }}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default UserListSection;
